import { TitleCasePipe } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { UntypedFormControl } from '@angular/forms'
import { VocabularyTerms } from '@ftr/contracts/type/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { LegacyTimeRangeComponent, TimeRangePrecision } from '@ftr/forms'
import {
  AnchorComponent,
  AnimatedRemoteComponent,
  ApiResult,
  ButtonColor,
  ButtonDisplayType,
  DestroySubscribers,
  ErrorRetryComponent,
  IconComponent,
  LineLoaderComponent,
  MOMENT_TIME_CONVERSION_FORMAT,
  TimeRange,
  isNotNullOrUndefined,
  mapData,
} from '@ftr/foundation'
import { VocabularyTermsService } from '@ftr/ui-vocab'
import { LocalDate } from '@js-joda/core'
import { Store } from '@ngxs/store'
import moment from 'moment-timezone'
import { BehaviorSubject, Observable, filter, switchMap } from 'rxjs'
import { PlayerService } from '../../services'
import { PlaybackState, SetSealingTimeRange } from '../../store'
import { SealMode, SealUnsealModalComponent } from '../seal-unseal-modal/seal-unseal-modal.component'

interface ViewModel {
  terms: VocabularyTerms
}

@Component({
  selector: 'ftr-sealing-toolbar-menu',
  templateUrl: 'sealing-toolbar-menu.component.html',
  styleUrls: ['./sealing-toolbar-menu.component.css'],
  standalone: true,
  imports: [
    AnchorComponent,
    AnimatedRemoteComponent,
    ErrorRetryComponent,
    IconComponent,
    LineLoaderComponent,
    SealUnsealModalComponent,
    LegacyTimeRangeComponent,
    TitleCasePipe,
  ],
})
export class SealingToolbarMenuComponent extends DestroySubscribers implements OnInit {
  @Input() audioId: Uuid
  @Input() courtSystemId: Uuid
  @Input() timeRange: TimeRange
  @Output() onCancel = new EventEmitter()

  @ViewChild(LegacyTimeRangeComponent) timeRangeComponent: LegacyTimeRangeComponent

  viewModel: ApiResult<ViewModel>
  sealingTimeRange: TimeRange | undefined
  sealMode: SealMode

  private readonly isModalOpenSubject = new BehaviorSubject(false)

  isModalOpen$ = this.isModalOpenSubject.asObservable()
  serverError: string | undefined
  submitAttempted = false

  readonly timeRangeControl = new UntypedFormControl()
  readonly refreshSubject = new BehaviorSubject(undefined)
  readonly buttonColor = ButtonColor
  readonly timeRangePrecision = TimeRangePrecision
  readonly buttonDisplayType = ButtonDisplayType
  readonly sealModes = SealMode

  constructor(
    private readonly vocabularyTermsService: VocabularyTermsService,
    private readonly store: Store,
    private readonly playerService: PlayerService,
  ) {
    super()
  }

  ngOnInit(): void {
    this.viewModel = this.setupViewModel()
  }

  setupViewModel(): ApiResult<ViewModel> {
    return this.refreshSubject.pipe(
      switchMap(() => ApiResult.from(this.vocabularyTermsService.observeTermsSnapshot(this.courtSystemId))),
      mapData(terms => ({ terms })),
    )
  }

  openSealingModal(sealMode: SealMode): void {
    this.sealMode = sealMode
    this.submitAttempted = true
    // only open the modal if there are valid time ranges
    this.getSealingTimeRange().subscribe(() => this.isModalOpenSubject.next(true))
  }

  private isSealingTimeFrameValid(): boolean {
    return !!(this.sealingTimeRange?.end && this.sealingTimeRange?.start?.isBefore(this.sealingTimeRange.end))
  }

  onSuccessfulSeal(): void {
    this.resetTimeRange()
    this.isModalOpenSubject.next(false)
  }

  cancel(): void {
    this.onCancel.emit()
  }

  private getSealingTimeRange(): Observable<TimeRange> {
    return this.store.selectOnce(PlaybackState.sealingTimeRange).pipe(filter(isNotNullOrUndefined))
  }

  private resetTimeRange(): void {
    this.submitAttempted = false
    this.timeRangeComponent.timeInputComponents.forEach(c => {
      c.onChange.emit(undefined)
      c.internalControl.reset('')
    })
  }

  setSealingTimeRange(timeRange: TimeRange): void {
    const startDate = this.playerService.startTime?.toLocalDate()
    this.sealingTimeRange = substituteTimeRangeDate(startDate, timeRange)
    this.store.dispatch(new SetSealingTimeRange(this.isSealingTimeFrameValid() ? this.sealingTimeRange : undefined))
  }
}

function substituteTimeRangeDate(date: LocalDate | undefined, timeRange: TimeRange): TimeRange {
  const dateString = date?.toString()
  if (dateString && timeRange.start && timeRange.end) {
    timeRange.start = moment(`${dateString}T${timeRange.start.format(MOMENT_TIME_CONVERSION_FORMAT)}`)
    timeRange.end = moment(`${dateString}T${timeRange.end.format(MOMENT_TIME_CONVERSION_FORMAT)}`)
  }
  return timeRange
}
